import type { TelephoneNumberModel } from './state/types';
import { Article } from 'pages/employee-assistance/types/articles';

import { AnalyticsEvent } from 'services/google-analytics/analytics-events';
import { getCurrentContentSection } from './helpers/get-current-content-section';

import { TelephoneNumberLabels } from './constants';
import { ANALYTICS_CONSTANTS } from 'services/google-analytics/constants';

import Envelope from 'wa-storybook/components/general/icon/components/envelope';
import Comment from 'wa-storybook/components/general/icon/components/comment';
import { restrictions } from 'state/features-list/constants';
import { handleRestrictions } from 'globals/restrictions-handler';
import { getEffectiveSettingsUrl, isSharedAccount } from 'stores/user-store';
import { goTo } from 'router/navigation';
import { HelpOption } from 'react-components/contact-boxout/contact-boxout';
import { isFeatureEnabled } from 'stores/features-list-store';

import icEnvelope from 'assets/images/icons/ic_envelope.svg';
import iconCalendar from 'assets/images/icons/ic_calendar.svg';
import icChat from 'assets/images/icons/ic_chat.svg';

export const getCurrentSectionTitle = () => {
  switch (getCurrentContentSection()) {
    case 'hwr':
      return polyglot.t('components.main_menu.hwr_toolkits');
    case 'employee-assistance':
      return polyglot.t('components.main_menu.employee_assistance');
    default:
      return '';
  }
};

export const isLifespeakArticle = (article: Article) => {
  return !!article.lifeSpeak && !!article.lifeSpeak.lifeSpeakUrl;
};

export const getPhoneNumberLabel = (phoneNumber: TelephoneNumberModel) => {
  switch (phoneNumber.label) {
    case TelephoneNumberLabels.TOLL_FREE:
    case TelephoneNumberLabels.TTY:
    case TelephoneNumberLabels.COLLECT:
    case TelephoneNumberLabels.CHARGE_APPLIED:
      return polyglot.t(
        `employee_assistance.phone_numbers.labels.${phoneNumber.label}`,
      );
    case TelephoneNumberLabels.NO_LABEL:
      return '';
    case TelephoneNumberLabels.CUSTOM:
    default:
      return phoneNumber.label_value;
  }
};

export interface HELP_OPTON extends HelpOption {
  iconSrc?: string;
}

const HELP_OPTIONS: Record<string, HELP_OPTON> = {
  workLifeServices: {
    action: () => getEffectiveSettingsUrl('work_life_services'),
    analyticsEvent: () =>
      AnalyticsEvent.Life.Eap.Modalities.GetSupport.clicked(
        ANALYTICS_CONSTANTS.LIFE.EAP.MODALITIES.GETSUPPORT.CLICKED.ITEM_NAME
          .WORK_LIFE_SERVICES,
      ),
    hook: 'work_life_services',
    restrictions: [restrictions.work_life_services],
    label: polyglot.t('online_booking.modalities.work_life_services'),
  },
  confidentialSupportRequest: {
    action: () => goTo('confidentialSupportRequest'),
    hook: 'confidentialSupportRequest',
    restrictions: [restrictions['submit_confidential_request_for_support']],
    label: polyglot.t('components.contact_boxout.confidential_request_form'),
    icon: () => <Envelope />,
    iconSrc: icEnvelope,
  },
  scheduleAppointment: {
    action: () => {
      if (isSharedAccount()) {
        return goTo('limitedAccount');
      } else if (isFeatureEnabled('counsellor_appointments')) {
        return goTo('appointmentsAppointmentIntro');
      } else {
        return window.open(
          getEffectiveSettingsUrl('online_scheduling'),
          '_blank',
          'noopener noreferrer',
        );
      }
    },
    analyticsEvent: () =>
      AnalyticsEvent.Life.Eap.Modalities.GetSupport.clicked(
        ANALYTICS_CONSTANTS.LIFE.EAP.MODALITIES.GETSUPPORT.CLICKED.ITEM_NAME
          .ONLINE_SCHEDULING,
      ),
    hook: 'bookOnlineSupport',
    restrictions: [
      restrictions['online_scheduling'],
      restrictions['counsellor_appointments'],
    ],
    operator: 'OR',
    label: polyglot.t('employee_assistance.let_us_help.book_online'),
    icon: () => <img src={iconCalendar} alt='' />,
    iconSrc: iconCalendar,
  },
  firstChat: {
    action: () => {
      if (isSharedAccount()) {
        return goTo('limitedAccount');
      }

      if (isFeatureEnabled('care_coordination_specialists_messaging')) {
        return goTo('chat/ccs/mobile');
      }

      return window.open(
        getEffectiveSettingsUrl('first_chat'),
        '_blank',
        'noopener noreferrer',
      );
    },

    analyticsEvent: () =>
      AnalyticsEvent.Life.Eap.Modalities.GetSupport.clicked(
        ANALYTICS_CONSTANTS.LIFE.EAP.MODALITIES.GETSUPPORT.CLICKED.ITEM_NAME
          .CHAT,
      ),
    hook: 'first_chat',
    restrictions: [
      restrictions['first_chat'],
      restrictions['onshore_chat_support'],
      restrictions['care_coordination_specialists_messaging'],
    ],
    operator: 'OR',
    additionalCheck: () => {
      if (isFeatureEnabled['care_coordination_specialists_messaging']) {
        return true;
      }

      return !!getEffectiveSettingsUrl('first_chat');
    },
    label: polyglot.t('online_booking.modalities.live_chat'),
    icon: () => <Comment />,
    iconSrc: icChat,
  },
};

export const getMainHelpOptions = (): {
  mainOptions: Record<string, HELP_OPTON>;
  contactOptions: HELP_OPTON[];
} => {
  const usedOptions = ['scheduleAppointment', 'workLifeServices'];

  const mainOptions = usedOptions.reduce((acc, key) => {
    const option = HELP_OPTIONS[key];

    if (
      handleRestrictions(option?.restrictions, option?.operator ?? 'AND')
        .showInMenu
    ) {
      acc[key] = {
        ...option,
      };
    }

    return acc;
  }, {});

  const exclude = usedOptions.reduce((acc, key) => {
    const option = HELP_OPTIONS[key];

    acc[option?.hook ?? ''] = true;
    return acc;
  }, {});

  const contactOptions = getHelpOptions(exclude);

  return {
    mainOptions,
    contactOptions,
  };
};

export const getHelpOptions = (
  exclude: {
    [key: string]: boolean;
  } = {},
): Array<HELP_OPTON> => {
  const options = Object.values(HELP_OPTIONS);

  return options.filter(
    option =>
      handleRestrictions(option.restrictions, option.operator ?? 'AND')
        .showInMenu &&
      !exclude[option.hook] &&
      (option.additionalCheck ? option.additionalCheck() : true),
  );
};
